
import { useCallback } from "react";
import { toast } from "sonner";
import { Investor, Contact } from "@/types/investor";
import { loadProcessingProgress, clearProcessingProgress } from "@/utils/investors/investorStateUtils";

/**
 * Hook for handling data reset operations
 */
export const useDataResetOperations = (
  setInvestors: (investors: Investor[]) => void,
  setProcessedIds: (processedIds: Set<string>) => void,
  saveDataToSupabaseAndLocal: (investors: Investor[], contacts: Contact[], processedIds: Set<string>) => Promise<boolean>
) => {
  /**
   * Handle reprocessing of data
   */
  const handleReprocess = useCallback(() => {
    // Get the current data from storage
    const { investors, contacts } = loadProcessingProgress();
    
    if (!investors || investors.length === 0) {
      toast.error("No data to reprocess. Please upload your Excel file first.");
      return;
    }
    
    // Clear processing progress
    clearProcessingProgress();
    setProcessedIds(new Set());
    
    // Reset the investors to their initial state (with contacts matched)
    // Ensure relevance scores are reset to 0 and enrichedWith is removed
    const resetInvestors = investors.map(investor => ({
      ...investor,
      relevanceScore: 0,
      lastRelevanceUpdate: null,
      enrichedWith: undefined
    }));
    
    setInvestors(resetInvestors);
    saveDataToSupabaseAndLocal(resetInvestors, contacts, new Set());
    
    toast.success("Data reset successfully. Ready for processing.");
  }, [setInvestors, setProcessedIds, saveDataToSupabaseAndLocal]);

  return {
    handleReprocess
  };
};
